import { getStorageItem, setStorageItem, WebStorageType } from "../../common-deprecated/utils/storageUtils";
import type { TradeInAemStateType } from "../redux/store";
import type { TradeInCsl } from "../csl/TradeInCsl";
import type { TradeInAemVehicleType } from "./types";

// ----------------------------------------------------------------------
// Helper types and consts
// ----------------------------------------------------------------------
const LOCAL_STORAGE_ID = "com.toyota.tme.t1.tradeInAem";

export type TradeInAemLocalStorageItemType = { previouslySearchedVehicles?: TradeInAemVehicleType[] };

export type TradeInAemLocalStorageParams = {
    country: string;
    language: string;
};

// ----------------------------------------------------------------------
// Storage fns
// ----------------------------------------------------------------------

export const getTradeInAemLocalStorage = (
    params: TradeInAemLocalStorageParams,
): TradeInAemLocalStorageItemType | null => {
    return getStorageItem({ ...params, storageId: LOCAL_STORAGE_ID, context: "new" }, WebStorageType.local);
};

export const setTradeInAemLocalStorage = (
    params: TradeInAemLocalStorageParams,
    storageItem: TradeInAemLocalStorageItemType,
): void => {
    setStorageItem({ ...params, storageId: LOCAL_STORAGE_ID, context: "new" }, storageItem, WebStorageType.local);
};

// ----------------------------------------------------------------------
// Previously searched vehicles
// ----------------------------------------------------------------------

export const getPreviouslySearchedVehicles = (params: TradeInAemLocalStorageParams): TradeInAemVehicleType[] => {
    return getTradeInAemLocalStorage(params)?.previouslySearchedVehicles || [];
};

export const setPreviouslySearchedVehicles = (
    params: TradeInAemLocalStorageParams,
    previouslySearchedVehicles?: TradeInAemVehicleType[],
): void => {
    return setTradeInAemLocalStorage(params, { previouslySearchedVehicles });
};

/**
 * Adds a vehicle to the previouslySearchedVehicles in the localstorage
 */
export const addToPreviouslySearchedVehiclesToStorage = (
    params: TradeInAemLocalStorageParams,
    csl: TradeInCsl,
    vehicle: TradeInAemVehicleType,
): void => {
    const previouslySearchedVehicles = getPreviouslySearchedVehicles(params);
    const alreadySaved =
        previouslySearchedVehicles.findIndex(
            (previouslySearchedVehicle) => csl.getVehicleKey(previouslySearchedVehicle) === csl.getVehicleKey(vehicle),
        ) !== -1;
    if (!alreadySaved) setPreviouslySearchedVehicles(params, [...previouslySearchedVehicles, vehicle]);
};

/**
 * Removes a vehicle from the previouslySearchedVehicles from the localstorage
 * @returns The new list of previouslySearchedVehicles
 */
export const removeFromPreviouslySearchedVehiclesFromStorage = (
    params: TradeInAemLocalStorageParams,
    csl: TradeInCsl,
    vehicle: TradeInAemVehicleType,
): TradeInAemVehicleType[] => {
    const previouslySearchedVehicles = getPreviouslySearchedVehicles(params);

    const filteredPreviouslySearchedVehicles = previouslySearchedVehicles.filter(
        (previouslySearchedVehicle) => csl.getVehicleKey(previouslySearchedVehicle) !== csl.getVehicleKey(vehicle),
    );
    setPreviouslySearchedVehicles(params, filteredPreviouslySearchedVehicles);

    return filteredPreviouslySearchedVehicles;
};

// ----------------------------------------------------------------------
// Helper fns
// ----------------------------------------------------------------------

export const tradeInAemLocalStorageParamSelector = ({
    commonSettings,
}: TradeInAemStateType): TradeInAemLocalStorageParams => ({
    country: commonSettings.country,
    language: commonSettings.language,
});
